import React, { FC } from "react";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Tabs,
  Row,
  Col,
  Button,
  InputNumber,
  Card,
  Typography,
  Spin,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import dayjs from "dayjs";
import DocumentUpload from "../../PreAuth/PreAuthEchs/DocumentUpload";
import ResponseDocumentUpload from "../../PreAuth/PreAuthEchs/ResponseDocumentUpload";
import { useUserFunctionalities } from "../../../../utils/UserFunctionalities";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../../utils/constants";

const { Title } = Typography;

interface ClaimStarHealthProps {
  form: any;
  isLoading: boolean;
  documentTypes: any[];
  documentUrls: any[];
  documentRequestResponseDocuments: any;
  handleFileChange: any;
  handleOpen: any;
  isEditable: boolean;
  isApproved: boolean;
  isSubmitting: boolean;
  isIntimating: boolean;
  isSaving: boolean;
  handleSaveConfirmationModal: any;
  handleSaveData: (values: any) => Promise<void>;
  claimData: any;
  currentStage: any;
  activeKey: string | string[];
  isApprovedInitiated: boolean;
  uploadFileList: any;
}

const ClaimStarHealth: FC<ClaimStarHealthProps> = ({
  form,
  isLoading,
  documentTypes,
  documentUrls,
  documentRequestResponseDocuments,
  handleFileChange,
  handleOpen,
  isEditable,
  isApproved,
  isSubmitting,
  isIntimating,
  isSaving,
  handleSaveConfirmationModal,
  handleSaveData,
  claimData,
  currentStage,
  activeKey,
  isApprovedInitiated,
  uploadFileList
}) => {
  const { TabPane } = Tabs;
  const { hasFunctionality } = useUserFunctionalities();
  const { auth, patients } = useSelector((state: RootState) => state);
  const navigate = useNavigate();

  const currencyFormatter = (value: number | undefined) =>
    value ? `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : "";

  const currencyParser = (value: string | undefined) => {
    const parsedValue = value?.replace(/₹\s?|(,*)/g, "");
    return parsedValue ? Number(parsedValue) : 0;
  };

  return (
    <Form form={form} layout="vertical">
      <Title level={4}>Claim Details</Title>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: "Please enter amount" },
              {
                pattern: /^\d+$/,
                message: "Only Numeric values are allowed!",
              },
            ]}
          >
            <Input disabled={claimData?.submitted} />
          </Form.Item>
        </Col>
      </Row>

      {isLoading ? (
        <div
          style={{
            margin: "auto",
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <>
          {documentRequestResponseDocuments?.["requestedDocuments"]?.length >
            0 && (
            <DocumentUpload
              documentTypes={documentTypes}
              documentUrls={documentUrls}
              handleFileChange={handleFileChange}
              handleOpen={handleOpen}
              documentRequestResponseDocuments={
                documentRequestResponseDocuments
              }
              portalSubmission={claimData?.submitted}
              isApproved={isApproved}
              uploadFileList={uploadFileList}
              isIntimated={claimData?.intimated}
            />
          )}

          {documentRequestResponseDocuments?.["responseDocuments"]?.length >
            0 && (
            <ResponseDocumentUpload
              documentTypes={documentTypes}
              documentUrls={documentUrls}
              handleFileChange={handleFileChange}
              handleOpen={handleOpen}
              portalSubmission={claimData?.submitted}
              documentRequestResponseDocuments={
                documentRequestResponseDocuments
              }
              handleSaveData={() => handleSaveData(form.getFieldsValue())}
              currentStage={currentStage}
              activeKey={activeKey}
              uploadFileList={uploadFileList}
            />
          )}
        </>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.8rem",
          marginTop: 16,
        }}
      >
        {claimData?.submitted || claimData?.initiated ? (
          <></>
        ) : !claimData?.id ? (
          hasFunctionality("claimCreation") && (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={() => handleSaveConfirmationModal("save")}
                loading={isSaving}
              >
                SAVE
              </Button>
            </div>
          )
        ) : (
          hasFunctionality("claimCreation") &&
          !(auth?.role?.role === "FO_STAFF" && claimData?.intimated) && (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={() => handleSaveConfirmationModal("update")}
                loading={isSaving}
              >
                Update
              </Button>
            </div>
          )
        )}

        {hasFunctionality("claimIntimation") &&
          (claimData?.intimated ||
          claimData?.submitted ||
          claimData?.initiated ||
          claimData?.approved ? (
            <></>
          ) : (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={() => handleSaveConfirmationModal("intimate")}
                loading={isIntimating}
              >
                Send To QC
              </Button>
            </div>
          ))}

        {patients?.fetchBasicDetails?.visitInfoResponse?.visitType?.toUpperCase() ===
          "OP" &&
          (hasFunctionality("opdBillList") ||
            hasFunctionality("ipdBillList")) && (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                  opacity: !isApproved ? 1 : 0.5,
                  pointerEvents: !isApproved ? "auto" : "none",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
                    navigate({
                      pathname: `${ROUTES.PATIENT_BILLING_LIST}/${patients?.fetchBasicDetails?.casesResponse?.id}/${patients?.fetchBasicDetails?.visitInfoResponse?.id}`,
                    });
                  }
                }}
              >
                Bill Audit
              </Button>
            </div>
          )}

        {hasFunctionality("claimApprover") &&
          !claimData?.initiated &&
          !claimData?.approved &&
          !claimData?.submitted && (
            <div style={{ width: "16%" }}>
              <Button
                loading={isApprovedInitiated}
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                  opacity: !isApproved ? 1 : 0.5,
                  pointerEvents: !isApproved ? "auto" : "none",
                }}
                // onClick={handleApprove}
                onClick={() => handleSaveConfirmationModal("approve")}
                disabled={isApproved}
              >
                Qc Approve
              </Button>
            </div>
          )}

        {claimData?.submitted || claimData?.initiated ? (
          <></>
        ) : (
          hasFunctionality("claimSubmission") && (
            <div style={{ width: "16%" }}>
              <Button
                type="primary"
                style={{
                  backgroundColor: "rgb(0, 76, 143)",
                  color: "#fff",
                  boxShadow: "0px 2px 8px 0px #00000040",
                  width: "100%",
                }}
                onClick={() => handleSaveConfirmationModal("submit")}
                loading={isSubmitting}
              >
                {patients?.fetchBasicDetails?.casesResponse?.integratedWithPortal ? "Initiate" : "Submit Manually"}
              </Button>
            </div>
          )
        )}
      </div>
    </Form>
  );
};

export default ClaimStarHealth;
